import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import mapStyle from '../../../assets/json/mapStyle.json';

// Office locations for the map
const officeLocations = [
  {
    id: 'london',
    title: 'London Office',
    coords: { lat: 51.521599, lng: -0.143610 },
  },
];

const loadGoogleMapsScript = callback => {
  const existingScript = document.getElementById('googleMapsApi');
  if (!existingScript) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_SECRET_KEY;
    if (!apiKey) return;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.id = 'googleMapsApi';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    script.onload = () => callback && callback();
  } else if (callback) callback();
};

const OfficeMap = ({ selectedOfficeId = null, height = '100%' }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    loadGoogleMapsScript(() => setScriptLoaded(true));
  }, []);

  useEffect(() => {
    if (!scriptLoaded || !window.google || !window.google.maps) return;
    setMapReady(false);
    const defaultCenter = officeLocations[0].coords; // London
    const defaultZoom = 15;
    // Create map instance
    const map = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: defaultZoom,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
      styles: mapStyle,
      backgroundColor: '#f7f7fa',
    });
    mapInstanceRef.current = map;
    // Add markers with hover effect
    markersRef.current = officeLocations.map(office => {
      const marker = new window.google.maps.Marker({
        position: office.coords,
        map,
        title: office.title,
        icon: {
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
          fillColor: '#6511af',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: 'white',
          scale: 1.5,
          anchor: new window.google.maps.Point(12, 22),
        },
      });
      // Add marker hover effect
      marker.addListener('mouseover', () => {
        marker.setIcon({
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
          fillColor: '#8e44ad',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: 'white',
          scale: 1.8,
          anchor: new window.google.maps.Point(12, 22),
        });
      });
      marker.addListener('mouseout', () => {
        marker.setIcon({
          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
          fillColor: '#6511af',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: 'white',
          scale: 1.5,
          anchor: new window.google.maps.Point(12, 22),
        });
      });
      return marker;
    });

    // Reveal map once initial render finishes
    if (window.google?.maps?.event) {
      window.google.maps.event.addListenerOnce(map, 'idle', () => setMapReady(true));
    } else {
      setMapReady(true);
    }
    // Center and zoom if selected
    if (selectedOfficeId) {
      const selected = officeLocations.find(o => o.id === selectedOfficeId);
      if (selected) {
        map.setCenter(selected.coords);
        map.setZoom(15);
      }
    } else {
      // Default to London office
      map.setCenter(officeLocations[0].coords);
      map.setZoom(15);
    }
    // Cleanup on unmount
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, [scriptLoaded, selectedOfficeId]);

  return (
    <Box
      sx={{
        width: '100%',
        height,
        position: 'relative',
        background: 'linear-gradient(135deg, #f7f7fa 0%, #e9e9e9 100%)',
        transition: 'height 0.3s',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
      }}
    >
      {!mapReady && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2, gap: 2, background: 'rgba(247, 247, 250, 0.9)' }}>
          <CircularProgress sx={{ color: '#6511af' }} />
          <Typography variant="body2" sx={{ color: '#6511af', fontWeight: 600 }}>
            Loading map...
          </Typography>
        </Box>
      )}
      <div ref={mapRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
    </Box>
  );
};

export default OfficeMap;
